/**
 * Columnas «como el Administrador de anuncios» (ajuste 10, `getmetacampaigns`).
 * Puro. Lee el bloque `ads_manager` (anuncio, campaña, `account_totals`, filas por
 * moneda) y, mientras la API no lo tenga desplegado, el bloque viejo `metrics`
 * (`impressions`, `ctr_pct`, `link_ctr_pct`, `cpc`, `cost_per_link_click`, `cpm`).
 * `metrics.frequency` NO se usa (APIBOT: cuenta personas repetidas). `null` → «—» (como Meta). Ver docs/maps/meta-ads.md § Ajuste 10.
 */
import { formatMetaMoney } from './metaAdsMoney';

/** Mismas columnas y etiquetas que `MetaAds/ads_manager.py` (si la API no manda el catálogo). */
export const META_ADS_MANAGER_DEFAULT_COLUMNS = [
  { key: 'impressions', label: 'Impresiones', type: 'count' },
  { key: 'reach', label: 'Alcance', type: 'count' },
  { key: 'frequency', label: 'Frecuencia', type: 'ratio' },
  { key: 'ctr_link_pct', label: 'CTR (porcentaje de clics en el enlace)', type: 'pct' },
  { key: 'ctr_all_pct', label: 'CTR (todos)', type: 'pct' },
  { key: 'cpc_link', label: 'CPC (costo por clic en el enlace)', type: 'money' },
  { key: 'cpc_all', label: 'CPC (todos)', type: 'money' },
  { key: 'cpm', label: 'CPM (costo por mil impresiones)', type: 'money' },
  { key: 'reach_last_30d', label: 'Alcance (últimos 30 días)', type: 'count' },
  { key: 'frequency_last_30d', label: 'Frecuencia (últimos 30 días)', type: 'ratio' },
];
const TYPES = ['count', 'ratio', 'pct', 'money'];
const MONEY_KEYS = ['cpc_link', 'cpc_all', 'cpm'];

/** Objetivo como lo muestra Meta (copia de `OBJECTIVE_LABELS_ES`), para la API sin `objective_label`. */
const OBJECTIVE_LABELS = {
  OUTCOME_AWARENESS: 'Reconocimiento',
  OUTCOME_TRAFFIC: 'Tráfico',
  OUTCOME_ENGAGEMENT: 'Interacción',
  OUTCOME_LEADS: 'Clientes potenciales',
  OUTCOME_APP_PROMOTION: 'Promoción de la app',
  OUTCOME_SALES: 'Ventas',
  BRAND_AWARENESS: 'Reconocimiento de marca',
  REACH: 'Alcance',
  LINK_CLICKS: 'Tráfico',
  POST_ENGAGEMENT: 'Interacción con la publicación',
  PAGE_LIKES: 'Me gusta de la página',
  EVENT_RESPONSES: 'Respuestas a eventos',
  VIDEO_VIEWS: 'Reproducciones de video',
  LEAD_GENERATION: 'Generación de clientes potenciales',
  MESSAGES: 'Mensajes',
  CONVERSIONS: 'Conversiones',
  PRODUCT_CATALOG_SALES: 'Ventas del catálogo',
  STORE_VISITS: 'Visitas al negocio',
  APP_INSTALLS: 'Instalaciones de la app',
};

const str = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null);
const num = (value) => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};
const day = (value) => (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null);

/** `objective_label` de la API; si no, la etiqueta conocida; si no, `objective` tal cual. */
export function metaObjectiveLabel(raw) {
  const label = str(raw?.objective_label);
  if (label) return label;
  const objective = str(raw?.objective);
  if (!objective) return null;
  return OBJECTIVE_LABELS[objective.toUpperCase()] || objective;
}

/**
 * Bloque listo para mostrar o `null` (sin `ads_manager` ni métricas viejas).
 * `source`: `'ads_manager'` (ajuste 10) o `'metrics'` (API vieja).
 *
 * @param {object} raw Anuncio / campaña / total / fila por moneda (con `ads_manager` y/o `metrics`).
 * @param {{ stripMoney?: boolean, metrics?: object|null }} [options] `stripMoney`: total con monedas
 *   mezcladas (la plata nunca se suma). `metrics`: bloque viejo si no está en `raw.metrics`.
 */
export function readMetaAdsManager(raw, { stripMoney = false, metrics = null } = {}) {
  const am = raw?.ads_manager && typeof raw.ads_manager === 'object' ? raw.ads_manager : null;
  let out = null;
  if (am) {
    out = {
      source: 'ads_manager',
      impressions: num(am.impressions),
      reach: num(am.reach),
      frequency: num(am.frequency),
      ctr_link_pct: num(am.ctr_link_pct),
      ctr_all_pct: num(am.ctr_all_pct),
      cpc_link: num(am.cpc_link),
      cpc_all: num(am.cpc_all),
      cpm: num(am.cpm),
      reach_last_30d: num(am.reach_last_30d),
      frequency_last_30d: num(am.frequency_last_30d),
      last30dSince: day(am.last_30d_since),
      last30dUntil: day(am.last_30d_until),
    };
  } else {
    const m = metrics || (raw?.metrics && typeof raw.metrics === 'object' ? raw.metrics : null);
    const legacy = m ? {
      impressions: num(m.impressions),
      ctr_link_pct: num(m.link_ctr_pct),
      ctr_all_pct: num(m.ctr_pct),
      cpc_link: num(m.cost_per_link_click),
      cpc_all: num(m.cpc),
      cpm: num(m.cpm),
    } : null;
    if (!legacy || Object.values(legacy).every((v) => v == null)) return null;
    out = {
      source: 'metrics',
      reach: null,
      frequency: null,
      reach_last_30d: null,
      frequency_last_30d: null,
      last30dSince: null,
      last30dUntil: null,
      ...legacy,
    };
  }
  if (stripMoney) MONEY_KEYS.forEach((k) => { out[k] = null; });
  return out;
}

/** `ads_manager_columns` de la API (`[{ key, label, type }]`) o las de siempre. */
export function readMetaAdsManagerColumns(data) {
  const raw = Array.isArray(data?.ads_manager_columns) ? data.ads_manager_columns : [];
  const cols = raw
    .map((c) => ({ key: str(c?.key), label: str(c?.label), type: TYPES.includes(c?.type) ? c.type : null }))
    .filter((c) => c.key && c.label && c.type);
  return cols.length ? cols : META_ADS_MANAGER_DEFAULT_COLUMNS;
}

const intFmt = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 });
const decFmt = new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Valor formateado (es-AR). `null` → «—»; `money` (CPC/CPM) con la moneda de la fila y siempre 2 decimales. */
export function formatMetaAdsManagerValue(type, value, currency) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const n = Number(value);
  if (type === 'count') return intFmt.format(n);
  if (type === 'ratio') return decFmt.format(n);
  if (type === 'pct') return `${decFmt.format(n)} %`;
  if (type === 'money') return formatMetaMoney(n, currency, { fixed2: true });
  return String(n);
}

const labelOf = (columns, key) => (columns.find((c) => c.key === key)
  || META_ADS_MANAGER_DEFAULT_COLUMNS.find((c) => c.key === key)).label;

/**
 * Celdas para la grilla del detalle: Impresiones, Alcance, Frecuencia, CTR, CPC, CPM.
 * Alcance / Frecuencia: `reach` si viene; si no, `reach_last_30d` rotulado
 * «(últimos 30 días)»; los dos `null` → «—» con la etiqueta del período.
 *
 * @returns {Array<{ key: string, label: string, value: string, reach?: boolean }>}
 */
export function metaAdsManagerCells(stats, currency, columns = META_ADS_MANAGER_DEFAULT_COLUMNS) {
  if (!stats) return [];
  const cell = (key, type) => ({ key, label: labelOf(columns, key), value: formatMetaAdsManagerValue(type, stats[key], currency) });
  const use30d = stats.reach == null && stats.reach_last_30d != null;
  const reachKey = use30d ? 'reach_last_30d' : 'reach';
  const freqKey = stats.frequency == null && stats.frequency_last_30d != null ? 'frequency_last_30d' : 'frequency';
  return [
    cell('impressions', 'count'),
    { ...cell(reachKey, 'count'), reach: true },
    { ...cell(freqKey, 'ratio'), reach: true },
    cell('ctr_link_pct', 'pct'),
    cell('ctr_all_pct', 'pct'),
    cell('cpc_link', 'money'),
    cell('cpc_all', 'money'),
    cell('cpm', 'money'),
  ];
}

/**
 * Ayuda en lenguaje simple para cada métrica (Tooltip del ícono ⓘ junto al rótulo).
 * Alcance y frecuencia usan el mismo texto para el período y para «últimos 30 días».
 */
export const META_ADS_MANAGER_HELP = {
  impressions: 'Cantidad de veces que se mostró el anuncio.',
  reach: 'Personas distintas que vieron el anuncio al menos una vez.',
  frequency: 'Promedio de veces que cada persona vio el anuncio.',
  ctr_link_pct: 'De cada 100 veces que se mostró, cuántas veces hicieron clic en el enlace.',
  ctr_all_pct: "Igual, pero contando cualquier clic (enlace, foto, perfil, 'ver más').",
  cpc_link: 'Costo promedio de cada clic en el enlace.',
  cpc_all: 'Costo promedio de cualquier clic.',
  cpm: 'Costo de mostrar el anuncio 1.000 veces.',
};

/**
 * Texto de ayuda de una métrica. Para el alcance suma `ads_manager_reach_note`
 * (por qué a veces es de los últimos 30 días) si la API la manda.
 */
export function metaAdsManagerHelp(key, reachNote = null) {
  const base = key.replace(/_last_30d$/, '');
  const text = META_ADS_MANAGER_HELP[base] || '';
  if (base === 'reach' && reachNote) return `${text} ${reachNote}`.trim();
  return text;
}

const GROUPS = [
  { key: 'reach', title: 'Alcance', keys: ['impressions', 'reach', 'frequency'] },
  { key: 'clicks', title: 'Clics', keys: ['ctr_link_pct', 'ctr_all_pct'] },
  { key: 'costs', title: 'Costos', keys: ['cpc_link', 'cpc_all', 'cpm'] },
];

/**
 * Filas agrupadas para la lista del detalle (Alcance · Clics · Costos), cada una con
 * rótulo completo de Meta, valor formateado («—» si null) y ayuda en lenguaje simple.
 *
 * @returns {Array<{ key: string, title: string, rows: Array<{ key: string, label: string, value: string, help: string }> }>}
 */
export function metaAdsManagerGroups(stats, currency, columns = META_ADS_MANAGER_DEFAULT_COLUMNS, reachNote = null) {
  const cells = metaAdsManagerCells(stats, currency, columns);
  if (!cells.length) return [];
  const byBase = Object.fromEntries(cells.map((c) => [c.key.replace(/_last_30d$/, ''), c]));
  return GROUPS.map((g) => ({
    key: g.key,
    title: g.title,
    rows: g.keys.map((k) => byBase[k]).filter(Boolean).map((c) => ({
      key: c.key, label: c.label, value: c.value, help: metaAdsManagerHelp(c.key, reachNote),
    })),
  }));
}

/**
 * Línea compacta (fila de anuncio / totales): «12.000 impresiones · CTR (enlace) 0,75 % ·
 * CPC (enlace) ARS 10 · CPM ARS 75». Sin bloque → ''. Con `short` sin las aclaraciones entre
 * paréntesis («CTR 0,75 % · CPC ARS 10 · CPM ARS 75»; la explicación va en el ⓘ,
 * `META_ADS_MANAGER_LINE_HELP`). Mismos valores.
 */
export function metaAdsManagerLine(stats, currency, { short = false } = {}) {
  if (!stats) return '';
  const link = short ? '' : ' (enlace)';
  return [
    `${formatMetaAdsManagerValue('count', stats.impressions, currency)} impresiones`,
    `CTR${link} ${formatMetaAdsManagerValue('pct', stats.ctr_link_pct, currency)}`,
    `CPC${link} ${formatMetaAdsManagerValue('money', stats.cpc_link, currency)}`,
    `CPM ${formatMetaAdsManagerValue('money', stats.cpm, currency)}`,
  ].join(' · ');
}

/** Texto único cuando el período no tiene nada (gasto, contactos, clics e impresiones en 0). */
export const META_NO_DATA_IN_PERIOD = 'Todavía no hay datos en este período';

/**
 * ¿El período está vacío? `true` solo si hay al menos un valor y todos los que vinieron son 0
 * (los `null` / sin dato no cuentan). Con cualquier valor distinto de 0 → `false` (se muestra como siempre).
 * No calcula nada: solo mira los números que ya vinieron de la API.
 *
 * @param {Array<number|null|undefined>} values Gasto, contactos, clics, impresiones… de todas las monedas.
 */
export function metaPeriodIsEmpty(values) {
  const present = (Array.isArray(values) ? values : []).filter((v) => v != null && v !== '' && Number.isFinite(Number(v)));
  return present.length > 0 && present.every((v) => Number(v) === 0);
}

/** Valores que miran el vacío de una campaña / anuncio (`metrics` + bloque del Administrador). */
export const metaActivityValues = (metrics, adsManager) => [
  metrics?.spend, metrics?.contacts, metrics?.clicks, metrics?.link_clicks, adsManager?.impressions,
];

const lowerFirst = (t) => t.charAt(0).toLowerCase() + t.slice(1);
/** ⓘ de la línea compacta: qué es cada sigla, con las definiciones de `META_ADS_MANAGER_HELP`. */
export const META_ADS_MANAGER_LINE_HELP = [
  `CTR (enlace): ${lowerFirst(META_ADS_MANAGER_HELP.ctr_link_pct)}`,
  `CPC (enlace): ${lowerFirst(META_ADS_MANAGER_HELP.cpc_link)}`,
  `CPM: ${lowerFirst(META_ADS_MANAGER_HELP.cpm)}`,
];
